import moneda from '../moneda';

export const BOT_NAME = 'BOT-HAIMPRESS';
export const MINIMUM_ORDER = 70000;
const SHOPHAI_LINE = 'ShopHai.online: tu tienda, tu compra mas facil.';

const STOP_WORDS = new Set([
    'a', 'al', 'algo', 'busco', 'buscar', 'con', 'de', 'del', 'el', 'en',
    'es', 'eso', 'la', 'las', 'le', 'lo', 'los', 'me', 'mi', 'para',
    'por', 'que', 'quiero', 'quisiera', 'un', 'una', 'unos', 'unas', 'ver',
]);

const ADMIN_TERMS = [
    'admin', 'administrador', 'dashboard', 'panel', 'clave', 'contrasena',
    'password', 'login', 'ruta privada', 'url privada', 'endpoint', 'php',
    'base de datos', 'acceso interno',
];

const RUDE_TERMS = [
    'bruto', 'bruta', 'tonto', 'tonta', 'idiota', 'bobo', 'boba',
    'estupido', 'estupida', 'imbecil', 'basura',
];

const BUYING_TERMS = [
    'comprar', 'compra', 'pedido', 'pedir', 'carrito', 'cedula', 'cc',
    'direccion', 'telefono', 'celular', 'registrar', 'datos',
    'pagar', 'pago', 'domicilio', 'envio',
];

const CONTACT_TERMS = [
    'whatsapp', 'soporte', 'asesor', 'asesora', 'contacto', 'contactar',
    'ayuda con pedido', 'garantia', 'cambio', 'reclamo', 'hablar con alguien',
    'hablar con una persona', 'atencion', 'business',
];

const OFFER_TERMS = ['oferta', 'ofertas', 'promo', 'promocion', 'descuento', 'rebaja'];
const MINIMUM_TERMS = ['minimo', 'minima', '70 mil', '70000', '70mil'];
const GREETING_TERMS = ['hola', 'buenas', 'hey', 'saludos'];

export function normalizeText(value = '') {
    return String(value)
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-zA-Z0-9]+/g, ' ')
        .toLowerCase()
        .trim();
}

export function tokenize(value = '') {
    return normalizeText(value)
        .split(' ')
        .filter(token => token && token.length > 1 && !STOP_WORDS.has(token));
}

export function formatCurrency(value) {
    return `${moneda} ${Number(value || 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`;
}

export function getProductImage(product) {
    return product?.imagen1 || product?.imagen2 || product?.imagen3 || product?.imagen4 || '';
}

export function productUrl(product) {
    const slug = normalizeText(product?.titulo || 'producto').replace(/\s+/g, '-') || 'producto';
    return `/producto/${product?.idProducto}/${slug}`;
}

export function getProductItems(product) {
    return Array.from({ length: 10 }, (_, index) => product?.[`item${index + 1}`]).filter(Boolean);
}

export function productNeedsOptions(product) {
    return normalizeText(product?.verItems) === 'si' && getProductItems(product).length > 0;
}

export function isOutOfStock(product) {
    const rawStock = String(product?.stock ?? '').trim();
    const normalizedStock = normalizeText(rawStock);

    if (!rawStock) return false;
    if (['agotado', 'sin stock', 'no disponible'].some(term => normalizedStock.includes(term))) return true;

    const numericStock = Number(rawStock);
    return Number.isFinite(numericStock) ? numericStock <= 0 : false;
}

export function isOfferProduct(product) {
    const currentPrice = Number(product?.precio || 0);
    const previousPrice = Number(product?.precioAnterior || 0);
    return previousPrice > currentPrice || normalizeText(product?.masVendido) === 'si';
}

export function addSimpleProductToCart(product, quantity = 1) {
    const cart = readCart();
    const productId = product?.idProducto;
    const existingIndex = cart.findIndex(item => String(item.idProducto) === String(productId));

    if (existingIndex >= 0) {
        cart[existingIndex] = {
            ...cart[existingIndex],
            cantidad: Number(cart[existingIndex].cantidad || 1) + quantity,
            items: Array.isArray(cart[existingIndex].items) ? cart[existingIndex].items : [],
        };
    } else {
        cart.push({ idProducto: productId, items: [], cantidad: quantity });
    }

    localStorage.setItem('cart', JSON.stringify(cart));
    window.dispatchEvent(new CustomEvent('cart:updated', { detail: { idProducto: productId } }));
    return cart;
}

export function openCartFromBot() {
    window.dispatchEvent(new CustomEvent('haimpress:open-cart'));
}

function readCart() {
    try {
        const stored = JSON.parse(localStorage.getItem('cart') || '[]');
        return Array.isArray(stored) ? stored : [];
    } catch (error) {
        return [];
    }
}

export function buildCatalog({ productos = [], secciones = [], categorias = [], subcategorias = [] }) {
    const categoryById = new Map(categorias.map(category => [String(category.idCategoria), category]));
    const sectionById = new Map(secciones.map(section => [String(section.idSeccion), section]));
    const subcategoryById = new Map(subcategorias.map(subcategory => [String(subcategory.idSubCategoria), subcategory]));

    const enrichedProducts = productos.map(product => {
        const category = categoryById.get(String(product.idCategoria)) || null;
        const subcategory = subcategoryById.get(String(product.idSubCategoria)) || null;
        const section = category?.idSeccion ? sectionById.get(String(category.idSeccion)) : null;

        return {
            ...product,
            categoryName: category?.categoria || product?.categoria || '',
            sectionName: section?.seccion || category?.seccion || '',
            subcategoryName: subcategory?.subcategoria || '',
        };
    });

    return {
        products: enrichedProducts,
        secciones,
        categorias,
        subcategorias,
    };
}

export function buildBotAnswer(query, catalog) {
    const normalizedQuery = normalizeText(query);
    const tokens = tokenize(query);

    if (!normalizedQuery) {
        return welcomeAnswer();
    }

    // Regla de seguridad: el asistente nunca entrega rutas, claves ni detalles internos.
    if (hasAnyTerm(normalizedQuery, ADMIN_TERMS)) {
        return {
            type: 'blocked',
            text: 'No puedo compartir accesos internos. Si haces parte del equipo, pide la ruta al administrador de la tienda.',
            products: [],
            navigationTargets: [],
            actions: defaultActions(),
        };
    }

    if (hasAnyTerm(normalizedQuery, RUDE_TERMS)) {
        return {
            type: 'redirect',
            text: 'Puede que no haya entendido bien. Dime que producto buscas y te guio con lo que esta disponible en la tienda.',
            products: [],
            navigationTargets: [],
            actions: defaultActions(),
        };
    }

    if (hasAnyTerm(normalizedQuery, GREETING_TERMS) && tokens.length <= 2) {
        return welcomeAnswer();
    }

    if (hasAnyTerm(normalizedQuery, CONTACT_TERMS)) {
        return contactHelpAnswer(normalizedQuery);
    }

    if (normalizedQuery === 'productos' || normalizedQuery.includes('todos los productos')) {
        return {
            type: 'help',
            text: `${SHOPHAI_LINE} Puedes revisar todo el catalogo y filtrar por seccion, categoria o precio.`,
            products: [],
            navigationTargets: [{ label: 'Ver todos los productos', url: '/productos' }],
            actions: [{ label: 'Como comprar', value: 'como comprar' }],
        };
    }

    if (normalizedQuery.includes('carrito')) {
        return {
            type: 'help',
            text: 'El carrito es donde revisas productos, completas tus datos y registras el pedido.',
            products: [],
            navigationTargets: [],
            actions: [{ label: 'Abrir carrito', value: 'ver carrito', action: 'open-cart' }],
        };
    }

    if (hasAnyTerm(normalizedQuery, MINIMUM_TERMS)) {
        return minimumHelpAnswer();
    }

    const navigationTargets = findNavigationTargets(normalizedQuery, tokens, catalog);
    const products = findMatchingProducts(normalizedQuery, tokens, catalog, navigationTargets[0]);

    if (products.length > 0 || navigationTargets.length > 0) {
        const targetText = navigationTargets[0] ? `Encontre ${navigationTargets[0].label}.` : 'Encontre estos productos.';
        return {
            type: 'results',
            text: `${targetText} Puedes abrir la seccion o tocar un producto para verlo.`,
            products,
            navigationTargets,
            actions: [
                { label: 'Como comprar', value: 'como comprar' },
                { label: 'Ver carrito', value: 'ver carrito', action: 'open-cart' },
            ],
        };
    }

    if (hasAnyTerm(normalizedQuery, BUYING_TERMS)) {
        return buyingHelpAnswer();
    }

    return {
        type: 'empty',
        text: 'No encontre ese producto en el catalogo. Cuentalo de otra forma o revisa todos los productos; puede que tu proxima compra este a un toque.',
        products: [],
        navigationTargets: [{ label: 'Ver todos los productos', url: '/productos' }],
        actions: defaultActions(),
    };
}

function welcomeAnswer() {
    return {
        type: 'welcome',
        text: `Hola, soy ${BOT_NAME} de ShopHai.online. Te ayudo a encontrar ese producto que tienes en mente y comprar sin perderte.`,
        products: [],
        navigationTargets: [],
        actions: defaultActions(),
    };
}

function contactHelpAnswer(normalizedQuery) {
    let whatsappMessage = 'Hola, necesito ayuda desde ShopHai.online.';

    if (normalizedQuery.includes('pedido')) {
        whatsappMessage = 'Hola, necesito ayuda con mi pedido en ShopHai.online.';
    }

    if (normalizedQuery.includes('garantia') || normalizedQuery.includes('cambio')) {
        whatsappMessage = 'Hola, necesito ayuda con una solicitud de garantia o cambio en ShopHai.online.';
    }

    return {
        type: 'contact',
        text: 'Para esa solicitud te conecto con soporte por WhatsApp. Alli una persona del equipo puede ayudarte mejor.',
        products: [],
        navigationTargets: [],
        actions: [
            { label: 'Abrir WhatsApp', action: 'open-whatsapp', whatsappMessage },
            { label: 'Seguir buscando', value: 'productos', url: '/productos' },
        ],
    };
}

function buyingHelpAnswer() {
    return {
        type: 'help',
        text: `${SHOPHAI_LINE} Para comprar, entra al producto, agregalo al carrito y alli completas nombre, cedula, celular, direccion o entrega y forma de pago. Al registrar el pedido queda guardado en el sistema y luego puedes enviar copia por WhatsApp.`,
        products: [],
        navigationTargets: [],
        actions: [
            { label: 'Ver productos', value: 'productos', url: '/productos' },
            { label: 'Ver carrito', value: 'ver carrito', action: 'open-cart' },
        ],
    };
}

function minimumHelpAnswer() {
    return {
        type: 'help',
        text: `En ShopHai.online compras con guia clara. Por ahora la compra minima es de ${formatCurrency(MINIMUM_ORDER)}. El carrito te ayuda a completar los datos antes de registrar el pedido.`,
        products: [],
        navigationTargets: [],
        actions: [
            { label: 'Buscar productos', value: 'productos', url: '/productos' },
            { label: 'Como comprar', value: 'como comprar' },
        ],
    };
}

function defaultActions() {
    return [
        { label: 'Ver ofertas', value: 'ofertas' },
        { label: 'Como comprar', value: 'como comprar' },
        { label: 'Hablar por WhatsApp', action: 'open-whatsapp', whatsappMessage: 'Hola, necesito ayuda desde ShopHai.online.' },
        { label: 'Ver productos', value: 'productos', url: '/productos' },
    ];
}

function hasAnyTerm(normalizedQuery, terms) {
    return terms.some(term => normalizedQuery.includes(normalizeText(term)));
}

function findNavigationTargets(normalizedQuery, tokens, catalog) {
    const targets = [];

    catalog.secciones.forEach(section => {
        if (matchesName(section.seccion, normalizedQuery, tokens) && hasProductsInSection(section, catalog)) {
            targets.push({
                type: 'section',
                label: `la seccion ${section.seccion}`,
                title: section.seccion,
                url: `/productos?seccion=${encodeURIComponent(section.idSeccion)}`,
                entity: section,
                score: scoreEntity(section.seccion, normalizedQuery, tokens),
            });
        }
    });

    catalog.categorias.forEach(category => {
        if (matchesName(category.categoria, normalizedQuery, tokens) && hasProductsInCategory(category, catalog)) {
            const params = new URLSearchParams();
            if (category.idSeccion) params.set('seccion', category.idSeccion);
            params.set('categoria', category.idCategoria);
            targets.push({
                type: 'category',
                label: `la categoria ${category.categoria}`,
                title: category.categoria,
                url: `/productos?${params.toString()}`,
                entity: category,
                score: scoreEntity(category.categoria, normalizedQuery, tokens) + 2,
            });
        }
    });

    catalog.subcategorias.forEach(subcategory => {
        if (matchesName(subcategory.subcategoria, normalizedQuery, tokens) && hasProductsInSubcategory(subcategory, catalog)) {
            const category = catalog.categorias.find(item => String(item.idCategoria) === String(subcategory.idCategoria));
            const params = new URLSearchParams();
            if (category?.idSeccion) params.set('seccion', category.idSeccion);
            if (subcategory.idCategoria) params.set('categoria', subcategory.idCategoria);
            params.set('subcategoria', subcategory.idSubCategoria);
            targets.push({
                type: 'subcategory',
                label: `la subcategoria ${subcategory.subcategoria}`,
                title: subcategory.subcategoria,
                url: `/productos?${params.toString()}`,
                entity: subcategory,
                score: scoreEntity(subcategory.subcategoria, normalizedQuery, tokens) + 3,
            });
        }
    });

    return targets
        .sort((a, b) => b.score - a.score)
        .slice(0, 2);
}

function findMatchingProducts(normalizedQuery, tokens, catalog, primaryTarget) {
    const priceLimit = extractPriceLimit(normalizedQuery);
    const offerIntent = hasAnyTerm(normalizedQuery, OFFER_TERMS);
    const cheapIntent = normalizedQuery.includes('barato') || normalizedQuery.includes('economico');

    let candidates = catalog.products.map(product => {
        const score = scoreProduct(product, normalizedQuery, tokens) + scoreTargetProduct(product, primaryTarget, catalog);
        return { product, score };
    });

    if (offerIntent) {
        const offerCandidates = candidates.filter(candidate => isOfferProduct(candidate.product));
        candidates = (offerCandidates.length > 0 ? offerCandidates : candidates).map(candidate => ({
            ...candidate,
            score: candidate.score + (isOfferProduct(candidate.product) ? 8 : 0),
        }));
    }

    if (priceLimit) {
        candidates = candidates.filter(candidate => Number(candidate.product?.precio || 0) <= priceLimit);
    }

    return candidates
        .filter(candidate => candidate.score > 0 || offerIntent || cheapIntent)
        .sort((a, b) => {
            if (cheapIntent || offerIntent) {
                return Number(a.product.precio || 0) - Number(b.product.precio || 0);
            }
            return b.score - a.score;
        })
        .map(candidate => candidate.product)
        .filter((product, index, list) => list.findIndex(item => String(item.idProducto) === String(product.idProducto)) === index)
        .slice(0, 4);
}

function scoreProduct(product, normalizedQuery, tokens) {
    const haystack = {
        title: normalizeText(product?.titulo),
        description: normalizeText(product?.descripcion),
        category: normalizeText(product?.categoryName),
        section: normalizeText(product?.sectionName),
        subcategory: normalizeText(product?.subcategoryName),
        items: normalizeText(getProductItems(product).join(' ')),
    };

    let score = 0;
    if (haystack.title.includes(normalizedQuery)) score += 10;
    if (haystack.category.includes(normalizedQuery)) score += 7;
    if (haystack.subcategory.includes(normalizedQuery)) score += 7;
    if (haystack.section.includes(normalizedQuery)) score += 5;

    tokens.forEach(token => {
        if (haystack.title.includes(token)) score += 5;
        if (haystack.category.includes(token)) score += 3;
        if (haystack.subcategory.includes(token)) score += 3;
        if (haystack.section.includes(token)) score += 2;
        if (haystack.items.includes(token)) score += 2;
        if (haystack.description.includes(token)) score += 1;
    });

    return score;
}

function scoreTargetProduct(product, target) {
    if (!target) return 0;

    if (target.type === 'section') {
        return String(product?.sectionName) && String(target.entity?.seccion) === String(product.sectionName) ? 6 : 0;
    }

    if (target.type === 'category') {
        return String(product?.idCategoria) === String(target.entity?.idCategoria) ? 8 : 0;
    }

    if (target.type === 'subcategory') {
        return String(product?.idSubCategoria) === String(target.entity?.idSubCategoria) ? 9 : 0;
    }

    return 0;
}

function matchesName(name, normalizedQuery, tokens) {
    const normalizedName = normalizeText(name);
    if (!normalizedName) return false;
    if (normalizedQuery.includes(normalizedName) || normalizedName.includes(normalizedQuery)) return true;

    const nameTokens = tokenize(normalizedName);
    return nameTokens.length > 0 && nameTokens.every(token => tokens.includes(token));
}

function scoreEntity(name, normalizedQuery, tokens) {
    const normalizedName = normalizeText(name);
    if (normalizedName === normalizedQuery) return 20;
    if (normalizedQuery.includes(normalizedName)) return 16;
    return tokenize(normalizedName).reduce((score, token) => score + (tokens.includes(token) ? 4 : 0), 0);
}

function extractPriceLimit(normalizedQuery) {
    const priceMatch = normalizedQuery.match(/(?:menos de|hasta|maximo|max|menor a)\s*(\d+(?:[.,]\d+)?)\s*(mil)?/);
    if (!priceMatch) return null;

    const rawNumber = Number(priceMatch[1].replace(/[.,]/g, ''));
    if (!Number.isFinite(rawNumber)) return null;

    return priceMatch[2] || rawNumber < 1000 ? rawNumber * 1000 : rawNumber;
}

function hasProductsInSection(section, catalog) {
    return catalog.products.some(product => normalizeText(product.sectionName) === normalizeText(section.seccion));
}

function hasProductsInCategory(category, catalog) {
    return catalog.products.some(product => String(product.idCategoria) === String(category.idCategoria));
}

function hasProductsInSubcategory(subcategory, catalog) {
    return catalog.products.some(product => String(product.idSubCategoria) === String(subcategory.idSubCategoria));
}
